import {
  updateStoreSettingsRequestSchema,
  type Currency,
  type StoreSettings,
} from '@avrahami/contracts';
import { parseWith, textOrNull } from './forms';

/**
 * The store's settings as one form. The settings page and the analytics page both edit this same
 * resource, and each sends the whole object back with the version it read (a stale version answers
 * CONFLICT).
 */
export interface SettingsDraft {
  siteOpen: boolean;
  ordersEnabled: boolean;
  deliveryEnabled: boolean;
  pickupEnabled: boolean;
  pickupAddress: string;
  guestCheckoutEnabled: boolean;
  registrationEnabled: boolean;
  showPrices: boolean;
  /** No screen edits it any more (stock is not managed here); it goes back as read. */
  lowStockThreshold: number;
  /** No screen edits it any more (adr/0022); the API still requires it, so it goes back as read. */
  freeShippingThresholdMinor: number | null;
  currency: Currency;
  timeZone: string;
  legalName: string;
  businessId: string;
  phone: string;
  whatsapp: string;
  email: string;
  address: string;
  website: string;
  seoTitle: string;
  seoDescription: string;
  ga4MeasurementId: string;
  metaPixelId: string;
}

export function settingsDraft(settings: StoreSettings): SettingsDraft {
  return {
    siteOpen: settings.siteOpen,
    ordersEnabled: settings.ordersEnabled,
    deliveryEnabled: settings.deliveryEnabled,
    pickupEnabled: settings.pickupEnabled,
    pickupAddress: settings.pickupAddress ?? '',
    guestCheckoutEnabled: settings.guestCheckoutEnabled,
    registrationEnabled: settings.registrationEnabled,
    showPrices: settings.showPrices,
    lowStockThreshold: settings.lowStockThreshold,
    freeShippingThresholdMinor: settings.freeShippingThresholdMinor,
    currency: settings.currency,
    timeZone: settings.timeZone,
    legalName: settings.business.legalName ?? '',
    businessId: settings.business.businessId ?? '',
    phone: settings.business.phone ?? '',
    whatsapp: settings.business.whatsapp ?? '',
    email: settings.business.email ?? '',
    address: settings.business.address ?? '',
    website: settings.business.website ?? '',
    seoTitle: settings.seo.title ?? '',
    seoDescription: settings.seo.description ?? '',
    ga4MeasurementId: settings.analytics.ga4MeasurementId ?? '',
    metaPixelId: settings.analytics.metaPixelId ?? '',
  };
}

export function settingsRequest(draft: SettingsDraft, version: number) {
  return parseWith(updateStoreSettingsRequestSchema, {
    siteOpen: draft.siteOpen,
    ordersEnabled: draft.ordersEnabled,
    deliveryEnabled: draft.deliveryEnabled,
    pickupEnabled: draft.pickupEnabled,
    pickupAddress: textOrNull(draft.pickupAddress),
    guestCheckoutEnabled: draft.guestCheckoutEnabled,
    registrationEnabled: draft.registrationEnabled,
    showPrices: draft.showPrices,
    lowStockThreshold: draft.lowStockThreshold,
    freeShippingThresholdMinor: draft.freeShippingThresholdMinor,
    currency: draft.currency,
    timeZone: draft.timeZone,
    business: {
      legalName: textOrNull(draft.legalName),
      businessId: textOrNull(draft.businessId),
      phone: textOrNull(draft.phone),
      whatsapp: textOrNull(draft.whatsapp),
      email: textOrNull(draft.email),
      address: textOrNull(draft.address),
      website: textOrNull(draft.website),
    },
    seo: {
      title: textOrNull(draft.seoTitle),
      description: textOrNull(draft.seoDescription),
    },
    analytics: {
      ga4MeasurementId: textOrNull(draft.ga4MeasurementId),
      metaPixelId: textOrNull(draft.metaPixelId),
    },
    version,
  });
}
